import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CallFrame } from '../components/CallFrame';
import { Button, Card, Loading, Screen } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { GuestHead } from './Bulawa';
import './guest.css';

/**
 * The guest in the call (§6). Plain Daily Prebuilt — no shop controls, no
 * screenshot button; those belong to the cart only.
 *
 * The guest waits in Daily's knocking room until the cart lets them in.
 */

type State =
  | { s: 'loading' }
  | { s: 'ready'; roomUrl: string; token: string; stub: boolean }
  | { s: 'gone'; kind: string }
  | { s: 'left' }
  | { s: 'error'; message: string };

export default function CallGuest() {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const [state, setState] = useState<State>({ s: 'loading' });

  const load = useCallback(async () => {
    try {
      const name = params.get('name') ?? '';
      const res = await api<{ state: string; room_url?: string; token?: string; stub?: boolean }>(
        `/api/g/call/${token}?name=${encodeURIComponent(name)}`
      );
      if (res.state === 'ready' && res.room_url && res.token) {
        setState({ s: 'ready', roomUrl: res.room_url, token: res.token, stub: Boolean(res.stub) });
      } else {
        setState({ s: 'gone', kind: res.state });
      }
    } catch (err) {
      setState({ s: 'error', message: err instanceof ApiError ? err.message : 'Could not open the call.' });
    }
  }, [token, params]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.s === 'loading') {
    return (
      <Screen>
        <GuestHead word="Mulaqaat" />
        <Card>
          <Loading label="Connecting you…" />
        </Card>
      </Screen>
    );
  }

  if (state.s === 'error' || state.s === 'gone') {
    const line =
      state.s === 'error'
        ? state.message
        : state.kind === 'not_started'
          ? 'The Mulaqaat has not started yet. Please keep this page open.'
          : state.kind === 'ended'
            ? 'This Mulaqaat has finished. Thank you for your time.'
            : 'This link has passed. Please phone the shop.';
    return (
      <Screen>
        <GuestHead word="Mulaqaat" />
        <Card>
          <p className="g__gone">{line}</p>
          <Button variant="secondary" onClick={() => void load()}>
            Try again
          </Button>
        </Card>
      </Screen>
    );
  }

  if (state.s === 'left') {
    return (
      <Screen>
        <GuestHead word="Mulaqaat" />
        <Card>
          <p className="g__gone">
            You have left the Mulaqaat. Thank you — the shop will be in touch.
          </p>
        </Card>
      </Screen>
    );
  }

  return (
    <Screen width="wide">
      <GuestHead word="Mulaqaat" />
      {state.stub ? (
        <Card className="g__stub">
          Video is in test mode on this server, so no picture will appear. Everything else is real.
        </Card>
      ) : (
        <CallFrame roomUrl={state.roomUrl} token={state.token} onLeft={() => setState({ s: 'left' })} />
      )}
      <p className="g__hint">
        You may see “waiting to be let in”. The shop will bring you through in a moment.
      </p>
    </Screen>
  );
}
